// Права текущего пользователя в активной организации — подсказка
// интерфейсу, а не авторизация (ADR-0080, `docs/spec/core/rbac.md`).
// Решает backend: секции записи сохраняют реактивную
// обработку `403`, потому что набор может устареть, а отказ дают и
// проверки вне набора.
//
// Неизвестный набор не даёт отказа (ADR-0080, п. 5): пока он грузится,
// последняя загрузка упала или посчитан не для активной организации,
// `isDenied` отвечает `false`. Упавшее перечитывание считается
// «неизвестно» даже при данных в кэше: TanStack оставляет прежние
// `data`, а доверять им после сбоя нельзя. Организацию ответа
// сверяем с `identity.orgId` стора — источником, из которого `scope.ts`
// строит ключ кэша: организацию могли сменить в соседней вкладке, и
// сервер посчитал набор для другой.
//
// Ключ `["permissions"]` стоит вне префикса `["me", …]`: префикс
// принадлежит `useMe`, его инвалидирует настройка remember-me, и права
// перечитывались бы вслед за ней. При смене организации запись уходит
// через `removeOrgScopedQueries`, потому что запрос org-scoped.

import { useMemo } from "react";

import { useAuthStore } from "../../auth/store";
import { useOrgScopedQuery } from "../../query/scope";
import { fetchOwnPermissions } from "./api";
import type { OwnPermissionsResponse } from "./api";

// Минута: секции записи, смонтированные вместе, делят один запрос, а
// смена роли видна при следующем монтировании через минуту; в
// промежутке устаревший набор прикрыт реактивным `403`. Дефолтный `0`
// перечитывал бы ручку на каждом новом потребителе; дольше нельзя: ADR
// связывает допустимую устарелость с частотой перечитывания.
const PERMISSIONS_STALE_TIME_MS = 60_000;

export interface MyPermissions {
  /** Набор применим: загружен, последняя загрузка не упала, посчитан для активной организации. */
  readonly isKnown: boolean;
  /** Право точно отсутствует: набор применим и кода в нём нет. Пока набор неприменим — `false` (ADR-0080, п. 5). */
  readonly isDenied: (code: string) => boolean;
}

export function useMyPermissions(): MyPermissions {
  const activeOrgId = useAuthStore((state) => state.identity?.orgId ?? null);
  const query = useOrgScopedQuery<OwnPermissionsResponse>({
    key: ["permissions"],
    queryFn: () => fetchOwnPermissions(),
    retry: false,
    staleTime: PERMISSIONS_STALE_TIME_MS,
  });
  const stale = query.isError || query.data?.org_id !== activeOrgId;
  const permissions = stale ? undefined : query.data?.permissions;
  // Мемо по ссылке на набор: потребители кладут `isDenied` в зависимости
  // эффектов и мемо, новый объект на каждом рендере их бы перезапускал.
  return useMemo(() => {
    const granted = permissions === undefined ? null : new Set(permissions);
    return {
      isKnown: granted !== null,
      isDenied: (code: string) => granted !== null && !granted.has(code),
    };
  }, [permissions]);
}
